import {
  ArrowLeft,
  Check,
  Code,
  Download,
  Eye,
  FileSearch,
  Loader2,
  Maximize2,
  RefreshCw,
  Save,
  Sparkles,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Markdown } from "@/components/shared/Markdown";
import { type OperationEvent, useOperationStream } from "@/hooks/useOperationStream";
import { sowiftService } from "@/lib/services";
import type { SowiftDocumentResponse, SowiftRefineAction } from "@/types";
import { REFINE_ACTIONS } from "./constants";

// ---------------------------------------------------------------------------
// Sub-components (extracted to reduce cognitive complexity)
// ---------------------------------------------------------------------------

/** Toolbar indicator showing current save state. */
function SaveIndicator({
  saving,
  hasUnsavedChanges,
  onManualSave,
}: Readonly<{
  saving: boolean;
  hasUnsavedChanges: boolean;
  onManualSave: () => void;
}>) {
  if (saving) {
    return (
      <span className="flex items-center gap-1 text-xs text-content-quaternary">
        <Loader2 className="h-3 w-3 animate-spin" />
        Saving...
      </span>
    );
  }
  if (hasUnsavedChanges) {
    return (
      <button
        onClick={onManualSave}
        className="flex items-center gap-1 text-xs text-brand-primary hover:text-brand-primary/80 transition-colors"
      >
        <Save className="h-3 w-3" />
        Save
      </button>
    );
  }
  return (
    <span className="flex items-center gap-1 text-xs text-content-quaternary">
      <Check className="h-3 w-3" />
      Saved
    </span>
  );
}

/** Preview button label + icon based on current state. */
function PreviewButtonContent({ previewing, showDocPreview }: { previewing: boolean; showDocPreview: boolean }) {
  if (previewing) {
    return (
      <>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Generating...
      </>
    );
  }
  if (showDocPreview) {
    return (
      <>
        <X className="h-3.5 w-3.5" />
        Close Preview
      </>
    );
  }
  return (
    <>
      <FileSearch className="h-3.5 w-3.5" />
      Preview Doc
    </>
  );
}

/** Main editor area — preview, edit, or split view. */
function EditorContent({
  viewMode,
  editContent,
  onContentChange,
  onTextSelection,
}: {
  viewMode: "preview" | "edit" | "split";
  editContent: string;
  onContentChange: (content: string) => void;
  onTextSelection: () => void;
}) {
  if (viewMode === "preview") {
    return (
      <div className="h-full overflow-auto bg-surface-primary border border-border-primary rounded-xl p-6">
        <Markdown content={editContent} />
      </div>
    );
  }
  if (viewMode === "edit") {
    return (
      <textarea
        value={editContent}
        onChange={(e) => onContentChange(e.target.value)}
        className="w-full h-full bg-surface-primary border border-border-primary rounded-xl p-6 text-sm text-content-primary font-mono resize-none focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
      />
    );
  }
  // split
  return (
    <div className="flex h-full gap-4">
      <textarea
        value={editContent}
        onChange={(e) => onContentChange(e.target.value)}
        className="w-1/2 bg-surface-primary border border-border-primary rounded-xl p-6 text-sm text-content-primary font-mono resize-none focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
      />
      <div // NOSONAR - preview pane captures text selection, not interactive
        className="w-1/2 overflow-auto bg-surface-primary border border-border-primary rounded-xl p-6"
        onMouseUp={onTextSelection}
        onKeyUp={onTextSelection}
      >
        <Markdown content={editContent} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SOWEditor({
  // NOSONAR -- editor component with refinement streaming, autosave, export, and preview state
  projectId,
  document: doc,
  documents,
  onDocumentSelect,
  onBack,
  onGenerate,
  onDocumentUpdate,
}: {
  projectId: string;
  document: SowiftDocumentResponse;
  documents: SowiftDocumentResponse[];
  onDocumentSelect: (doc: SowiftDocumentResponse) => void;
  onBack: () => void;
  onGenerate: () => void;
  onDocumentUpdate: (doc: SowiftDocumentResponse) => void;
}) {
  const [viewMode, setViewMode] = useState<"preview" | "edit" | "split">("preview");
  const [editContent, setEditContent] = useState(doc.contentMarkdown);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Refinement state
  const [selectedText, setSelectedText] = useState("");
  const [showRefineMenu, setShowRefineMenu] = useState(false);
  const [refineAction, setRefineAction] = useState<SowiftRefineAction | null>(null);
  const [customInstruction, setCustomInstruction] = useState("");
  const [refining, setRefining] = useState(false);
  const [refinedContent, setRefinedContent] = useState("");
  const [showRefineResult, setShowRefineResult] = useState(false);

  // Operation stream for refinement
  const [refineOperationId, setRefineOperationId] = useState<string | null>(null);
  const refineContentRef = useRef("");

  const handleRefineEvent = useCallback((event: OperationEvent) => {
    const { event: eventName, data } = event;

    switch (eventName) {
      case "content_delta": {
        const delta = (data as { delta: string }).delta;
        refineContentRef.current += delta;
        setRefinedContent(refineContentRef.current);
        break;
      }
      case "refine_end":
      case "operation_completed": {
        setRefining(false);
        setRefineOperationId(null);
        break;
      }
      case "operation_failed": {
        console.error("Refine operation failed:", (data as { error?: string }).error);
        setRefining(false);
        setRefineOperationId(null);
        refineContentRef.current = "";
        break;
      }
    }
  }, []);

  useOperationStream(refineOperationId, {
    onEvent: handleRefineEvent,
    onError: (err) => {
      console.error("Refine operation stream error:", err);
      setRefining(false);
      setRefineOperationId(null);
      refineContentRef.current = "";
    },
  });

  // Export state
  const [exporting, setExporting] = useState(false);
  const [exportTemplate, setExportTemplate] = useState<"branded" | "clean">("branded");

  // Document preview state
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showDocPreview, setShowDocPreview] = useState(false);
  const [previewHeight, setPreviewHeight] = useState(0);
  const previewContainerRef = useRef<HTMLDivElement>(null);

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset editor content when document changes
  useEffect(() => {
    setEditContent(doc.contentMarkdown);
    setHasUnsavedChanges(false);
  }, [doc.uuid]);

  const handleContentChange = (newContent: string) => {
    setEditContent(newContent);
    setHasUnsavedChanges(newContent !== doc.contentMarkdown);

    // Debounced auto-save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveDocument(newContent);
    }, 2000);
  };

  const saveDocument = async (content?: string) => {
    try {
      setSaving(true);
      const response = await sowiftService.updateDocument(projectId, doc.uuid, {
        contentMarkdown: content ?? editContent,
      });
      onDocumentUpdate(response.data);
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Failed to save document:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleManualSave = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveDocument();
  };

  const handleToggleStatus = async () => {
    const newStatus = doc.status === "draft" ? "final" : "draft";
    try {
      setSaving(true);
      const response = await sowiftService.updateDocument(projectId, doc.uuid, {
        contentMarkdown: editContent,
        status: newStatus,
      });
      onDocumentUpdate(response.data);
    } catch (err) {
      console.error("Failed to update status:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleTextSelection = () => {
    const selection = globalThis.getSelection();
    const text = selection?.toString().trim();
    if (text && text.length > 10) {
      setSelectedText(text);
      setShowRefineMenu(true);
    } else {
      setShowRefineMenu(false);
    }
  };

  const handleRefine = async (action: SowiftRefineAction) => {
    if (!selectedText) return;

    if (action === "custom" && !customInstruction.trim()) {
      setRefineAction("custom");
      return;
    }

    try {
      setRefining(true);
      setRefineAction(action);
      setRefinedContent("");
      setShowRefineResult(true);
      setShowRefineMenu(false);
      refineContentRef.current = "";

      const result = await sowiftService.refineSection(projectId, {
        sectionMarkdown: selectedText,
        action,
        customInstructions: action === "custom" ? customInstruction : undefined,
      });
      setRefineOperationId(result.operation_id);
    } catch (err) {
      console.error("Failed to refine section:", err);
      setRefining(false);
    }
  };

  const acceptRefinement = () => {
    if (!refinedContent || !selectedText) return;
    const newContent = editContent.replace(selectedText, refinedContent);
    setEditContent(newContent);
    setHasUnsavedChanges(true);
    setShowRefineResult(false);
    setSelectedText("");
    setRefinedContent("");
    setRefineAction(null);
    setCustomInstruction("");

    // Save after accepting
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveDocument(newContent);
    }, 500);
  };

  const rejectRefinement = () => {
    setShowRefineResult(false);
    setRefinedContent("");
    setRefineAction(null);
    setCustomInstruction("");
  };

  const handleExportDocx = async () => {
    try {
      setExporting(true);
      const blob = await sowiftService.exportDocument(projectId, doc.uuid, {
        template: exportTemplate,
      });
      // Trigger browser download
      const url = URL.createObjectURL(blob);
      const a = globalThis.document.createElement("a");
      a.href = url;
      a.download = `SOW_v${doc.version}.docx`;
      globalThis.document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to export document:", err);
    } finally {
      setExporting(false);
    }
  };

  const handlePreviewDocument = useCallback(async () => {
    try {
      setPreviewing(true);
      // Revoke previous preview URL if it exists
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      const blob = await sowiftService.previewDocument(projectId, doc.uuid, {
        template: exportTemplate,
      });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setShowDocPreview(true);
    } catch (err) {
      console.error("Failed to preview document:", err);
    } finally {
      setPreviewing(false);
    }
  }, [projectId, doc.uuid, exportTemplate, previewUrl]);

  const closeDocPreview = useCallback(() => {
    setShowDocPreview(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  }, [previewUrl]);

  // Clean up preview URL on unmount or document change
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Dynamically compute available height for the preview iframe.
  // The CSS height chain breaks at <main> (display:block), so percentage-based
  // heights don't propagate. Instead we measure the container's position and
  // subtract from the viewport height, accounting for the footer.
  useEffect(() => {
    if (!showDocPreview) return;

    const updateHeight = () => {
      const el = previewContainerRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      // 48px accounts for the footer + bottom padding
      setPreviewHeight(Math.max(200, globalThis.innerHeight - top - 48));
    };

    updateHeight();

    globalThis.addEventListener("resize", updateHeight);
    // Also observe the container in case layout shifts
    const observer = new ResizeObserver(updateHeight);
    if (previewContainerRef.current) {
      observer.observe(previewContainerRef.current);
    }

    return () => {
      globalThis.removeEventListener("resize", updateHeight);
      observer.disconnect();
    };
  }, [showDocPreview]);

  return (
    <div className="flex flex-col h-full -mt-2">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-sm text-content-tertiary hover:text-content-primary transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            All Documents
          </button>

          {/* Version Selector */}
          {documents.length > 1 && (
            <select
              value={doc.uuid}
              onChange={(e) => {
                const selected = documents.find((d) => d.uuid === e.target.value);
                if (selected) onDocumentSelect(selected);
              }}
              className="px-2 py-1 bg-surface-secondary border border-border-secondary rounded-sm text-xs text-content-primary focus:outline-hidden"
            >
              {documents.map((d) => (
                <option key={d.uuid} value={d.uuid}>
                  v{d.version} - {d.title}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="flex items-center gap-2">
          <SaveIndicator saving={saving} hasUnsavedChanges={hasUnsavedChanges} onManualSave={handleManualSave} />

          {/* Status Toggle */}
          <button
            onClick={handleToggleStatus}
            className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
              doc.status === "final"
                ? "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                : "bg-surface-tertiary text-content-secondary hover:bg-interactive-active"
            }`}
          >
            {doc.status === "final" ? "Final" : "Draft"}
          </button>

          {/* View Mode Toggle */}
          <div className="flex items-center border border-border-secondary rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode("preview")}
              className={`p-1.5 ${viewMode === "preview" ? "bg-surface-tertiary text-content-primary" : "text-content-tertiary hover:text-content-primary"}`}
              title="Preview"
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode("split")}
              className={`p-1.5 ${viewMode === "split" ? "bg-surface-tertiary text-content-primary" : "text-content-tertiary hover:text-content-primary"}`}
              title="Split View"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode("edit")}
              className={`p-1.5 ${viewMode === "edit" ? "bg-surface-tertiary text-content-primary" : "text-content-tertiary hover:text-content-primary"}`}
              title="Edit"
            >
              <Code className="h-4 w-4" />
            </button>
          </div>

          {/* Re-generate */}
          <button
            onClick={onGenerate}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-secondary hover:bg-interactive-active border border-border-secondary rounded-lg text-xs text-content-primary transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Re-generate
          </button>

          {/* Export Template, Preview & DOCX */}
          <select
            value={exportTemplate}
            onChange={(e) => setExportTemplate(e.target.value as "branded" | "clean")}
            className="px-2 py-1.5 bg-surface-secondary border border-border-secondary rounded-lg text-xs text-content-primary focus:outline-hidden focus:border-border-hover"
            title="Export template style"
          >
            <option value="branded">Branded</option>
            <option value="clean">Clean</option>
          </select>
          <button
            onClick={showDocPreview ? closeDocPreview : handlePreviewDocument}
            disabled={previewing}
            className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs transition-colors disabled:opacity-50 ${
              showDocPreview
                ? "bg-brand-secondary/20 hover:bg-brand-secondary/30 border-brand-secondary/40 text-brand-secondary"
                : "bg-surface-secondary hover:bg-interactive-active border-border-secondary text-content-primary"
            }`}
          >
            <PreviewButtonContent previewing={previewing} showDocPreview={showDocPreview} />
          </button>
          <button
            onClick={handleExportDocx}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-primary-subtle hover:bg-brand-primary/30 border border-brand-primary/40 rounded-lg text-xs text-brand-primary transition-colors disabled:opacity-50"
          >
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            {exporting ? "Exporting..." : "Download DOCX"}
          </button>
        </div>
      </div>

      {/* Refine Menu (floating) */}
      {showRefineMenu && selectedText && !showRefineResult && (
        <div className="mb-3 shrink-0">
          <div className="bg-surface-secondary border border-border-secondary rounded-xl p-3">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-brand-primary" />
              <span className="text-xs font-medium text-content-primary">Refine Selected Text</span>
              <button
                onClick={() => {
                  setShowRefineMenu(false);
                  setSelectedText("");
                }}
                className="ml-auto p-0.5 text-content-quaternary hover:text-content-primary"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {REFINE_ACTIONS.map((ra) => (
                <button
                  key={ra.action}
                  onClick={() => handleRefine(ra.action)}
                  className="px-3 py-1.5 bg-surface-tertiary hover:bg-interactive-active rounded-lg text-xs text-content-primary transition-colors"
                  title={ra.description}
                >
                  {ra.label}
                </button>
              ))}
            </div>
            {refineAction === "custom" && (
              <div className="mt-2 flex gap-2">
                <input
                  type="text"
                  value={customInstruction}
                  onChange={(e) => setCustomInstruction(e.target.value)}
                  placeholder="Enter custom instructions..."
                  className="flex-1 px-3 py-1.5 bg-surface-primary border border-border-hover rounded-lg text-xs text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-1 focus:ring-brand-primary/50"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && customInstruction.trim()) {
                      handleRefine("custom");
                    }
                  }}
                  autoFocus
                />
                <button
                  onClick={() => handleRefine("custom")}
                  disabled={!customInstruction.trim()}
                  className="px-3 py-1.5 bg-brand-primary-subtle text-brand-primary rounded-lg text-xs hover:bg-brand-primary/30 disabled:opacity-50"
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Refinement Result */}
      {showRefineResult && (
        <div className="mb-3 shrink-0">
          <div className="bg-surface-secondary border border-brand-secondary/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-brand-secondary" />
                <span className="text-xs font-medium text-content-primary">
                  Refined ({REFINE_ACTIONS.find((r) => r.action === refineAction)?.label})
                </span>
                {refining && <Loader2 className="h-3 w-3 text-brand-secondary animate-spin" />}
              </div>
              {!refining && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={rejectRefinement}
                    className="px-3 py-1 bg-surface-tertiary hover:bg-interactive-active rounded-lg text-xs text-content-primary transition-colors"
                  >
                    Discard
                  </button>
                  <button
                    onClick={acceptRefinement}
                    className="px-3 py-1 bg-green-600 hover:bg-green-700 rounded-lg text-xs text-white transition-colors"
                  >
                    Accept
                  </button>
                </div>
              )}
            </div>
            <div className="bg-surface-primary rounded-lg p-3 max-h-48 overflow-auto">
              <Markdown content={refinedContent || "*Refining...*"} isStreaming />
            </div>
          </div>
        </div>
      )}

      {/* Editor Content / Document Preview */}
      {showDocPreview && previewUrl ? (
        <div
          ref={previewContainerRef}
          className="overflow-hidden"
          style={previewHeight ? { height: previewHeight } : undefined}
        >
          <iframe
            src={previewUrl}
            className="w-full h-full rounded-xl border border-border-primary bg-white"
            title="Document Preview"
          />
        </div>
      ) : (
        <div
          className="flex-1 min-h-0"
          onMouseUp={viewMode === "preview" ? handleTextSelection : undefined}
          onKeyUp={viewMode === "preview" ? handleTextSelection : undefined}
        >
          <EditorContent
            viewMode={viewMode}
            editContent={editContent}
            onContentChange={handleContentChange}
            onTextSelection={handleTextSelection}
          />
        </div>
      )}
    </div>
  );
}
